import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button, Spinner } from '@/components/ui';
import { getHrOverview, type HrOverview } from '@/api/services/hrWorkspace.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import type { HrArea } from '../../hrAreas';
import type { HrAreaProps } from '../hrShared';

export type HrGoTo = (area: HrArea, params?: Record<string, string>) => void;

interface AttentionItem {
  key: string;
  count: number;
  /** Reads after the count, for example "3 people aren't set up yet". */
  one: string;
  many: string;
  action: string;
  to: [HrArea, Record<string, string>?];
}

function attentionItems(overview: HrOverview): AttentionItem[] {
  const items: AttentionItem[] = [
    {
      key: 'overdue-probation',
      count: overview.probation?.overdue ?? 0,
      one: 'probation is past its end date with no decision',
      many: 'probations are past their end date with no decision',
      action: 'Decide',
      to: ['changes', { view: 'probation', bucket: 'overdue' }],
    },
    {
      key: 'job-change-failed',
      count: overview.jobChanges?.failed ?? 0,
      one: 'approved job change failed to apply',
      many: 'approved job changes failed to apply',
      action: 'Fix',
      to: ['changes', { status: 'failed' }],
    },
    {
      key: 'job-change-blocked',
      count: overview.jobChanges?.blocked ?? 0,
      one: 'job change has no one to approve it',
      many: 'job changes have no one to approve them',
      action: 'Fix',
      to: ['changes', { status: 'blocked' }],
    },
    {
      key: 'not-set-up',
      count: overview.notSetUp ?? 0,
      one: "person isn't set up yet",
      many: "people aren't set up yet",
      action: 'Set up',
      to: ['people', { status: 'not_set_up' }],
    },
    {
      key: 'due-probation',
      count: overview.probation?.due ?? 0,
      one: 'probation ends within 14 days',
      many: 'probations end within 14 days',
      action: 'Review',
      to: ['changes', { view: 'probation', bucket: 'due' }],
    },
    {
      key: 'job-change-approval',
      count: overview.jobChanges?.awaitingApproval ?? 0,
      one: 'job change is waiting for approval',
      many: 'job changes are waiting for approval',
      action: 'Review',
      to: ['changes', { status: 'submitted' }],
    },
    {
      key: 'discipline',
      count: overview.openCases?.discipline ?? 0,
      one: 'discipline case is open',
      many: 'discipline cases are open',
      action: 'Open',
      to: ['cases', { kind: 'discipline' }],
    },
    {
      key: 'grievance',
      count: overview.openCases?.grievance ?? 0,
      one: 'grievance is open',
      many: 'grievances are open',
      action: 'Open',
      to: ['cases', { kind: 'grievance' }],
    },
    {
      key: 'acknowledgements',
      count: overview.overdueAcknowledgements ?? 0,
      one: 'policy acknowledgement is overdue',
      many: 'policy acknowledgements are overdue',
      action: 'Chase',
      to: ['letters', { view: 'policies' }],
    },
    {
      key: 'signatures',
      count: overview.lettersAwaitingSignature ?? 0,
      one: 'letter is waiting for a signature',
      many: 'letters are waiting for a signature',
      action: 'View',
      to: ['letters', { status: 'issued' }],
    },
  ];
  return items.filter((item) => item.count > 0);
}

/** Where to go for the jobs HR comes here to do. Each entry needs its area. */
const JOBS: Array<{ area: HrArea; title: string; description: string; cta: string; params?: Record<string, string> }> = [
  {
    area: 'people',
    title: 'People',
    description: "Find anyone, open their profile and history, or see who reports to whom.",
    cta: 'Find a person',
  },
  {
    area: 'changes',
    title: 'Job changes',
    description: "Change someone's job, department, manager or pay with approval, and decide probations.",
    cta: 'Start a job change',
    params: { new: '1' },
  },
  {
    area: 'letters',
    title: 'Letters & policies',
    description: 'Send letters for signature and publish policies people must acknowledge.',
    cta: 'Generate a letter',
    params: { new: '1' },
  },
  {
    area: 'cases',
    title: 'Cases',
    description: 'Handle discipline and grievances in confidence. Each case is visible to its team only.',
    cta: 'See cases',
  },
  {
    area: 'setup',
    title: 'Setup',
    description: 'Set the departments, job titles, letter templates and rules each company uses.',
    cta: 'Open setup',
  },
];

/**
 * The page HR lands on: what needs doing today, then what this module is for.
 * Everything links straight to the work.
 */
export function HrOverviewTab({
  companyIds,
  allowedAreas,
  goTo,
}: Pick<HrAreaProps, 'companyIds'> & {
  allowedAreas: HrArea[];
  goTo: HrGoTo;
}): React.ReactElement {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-overview', companyIds?.join(',') ?? ''],
    queryFn: () => getHrOverview(companyIds),
    refetchOnMount: 'always',
  });
  const items = data ? attentionItems(data) : [];
  const jobs = JOBS.filter((job) => allowedAreas.includes(job.area));

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <section aria-labelledby="hr-attention-heading" className="min-w-0">
        <h2 id="hr-attention-heading" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Needs your attention
        </h2>
        <div className="mt-3 rounded-[var(--radius-panel)] border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-800">
          {isLoading ? (
            <div className="flex justify-center py-10" role="status">
              <Spinner />
              <span className="sr-only">Loading what needs attention</span>
            </div>
          ) : error ? (
            <div className="space-y-3 p-5" role="alert">
              <p className="text-sm text-danger">{getErrorMessage(error)}</p>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <p className="flex items-center gap-2 px-5 py-8 text-sm text-gray-700 dark:text-gray-200">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              Nothing needs you right now.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => goTo(item.to[0], item.to[1])}
                    className="group flex w-full items-center gap-4 px-5 py-3 text-left transition-colors first:rounded-t-[var(--radius-panel)] last:rounded-b-[var(--radius-panel)] hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary dark:hover:bg-gray-700/40"
                  >
                    <span className="w-10 shrink-0 text-right text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                      {item.count}
                    </span>
                    <span className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-200">
                      {item.count === 1 ? item.one : item.many}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-primary dark:text-sky-400">
                      {item.action}
                      <ArrowRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section aria-labelledby="hr-jobs-heading" className="min-w-0">
        <h2 id="hr-jobs-heading" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          What you can do here
        </h2>
        <ul className="mt-3 space-y-4">
          {jobs.map((job) => (
            <li key={job.area}>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{job.title}</p>
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{job.description}</p>
              <button
                type="button"
                onClick={() => goTo(job.area, job.params)}
                className="mt-1 text-sm font-medium text-primary underline-offset-2 hover:underline dark:text-sky-400"
              >
                {job.cta}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
